import { filterPossibleUsersForJob } from 'server/helpers/filterPossibleUsersForJob';
import { queryPossibleUsersForJob } from 'server/helpers/queryPossibleUsersForJob';
import { Organisations } from 'server/shared/collections/organisations';
import { PoolerCustomerMap } from 'server/shared/helpers/PoolerMap';
import { Customer } from 'server/shared/models/customer';
import { Department } from 'server/shared/models/department';
import { Job } from 'server/shared/models/job';
import { Shift } from 'server/shared/models/shift';
import { Transfer } from 'server/shared/models/transfer';
import { TranslationKeyTranslation } from 'server/shared/models/translation';
import { DispoUser } from 'server/shared/models/user';
import { JobMatchPoolerUser } from '../../app/jobs/job-edit/job-edit.types';
import { getJobMatchPooler } from './getJobMatchPooler';

export interface GetPossibleUsersForJobParams {
  date: string;
  job: Job;
  shifts: Shift[];
  department: Department;
  loggedInCustomer: Customer;
  sharedPoolCustomers: Customer[];
  poolerMap: PoolerCustomerMap;
  user: DispoUser;
  i18n: TranslationKeyTranslation;
  isOwnJob: boolean;
  undecided?: boolean;
  ignoreUserPreferenceDepartmentBlacklist?: boolean;
  jobInPast?: boolean;
  search?: string;
  transferToMe?: Transfer;
}

export function getPossibleUsersForJob({
  date,
  job,
  shifts,
  department,
  loggedInCustomer,
  sharedPoolCustomers,
  poolerMap,
  user,
  i18n,
  isOwnJob,
  undecided = false,
  ignoreUserPreferenceDepartmentBlacklist = false,
  jobInPast = false,
  search,
  transferToMe,
}: GetPossibleUsersForJobParams): JobMatchPoolerUser[] {
  if (!user || !user.customerId || !loggedInCustomer) {
    return [];
  }
  const customers = [
    loggedInCustomer?._id ?? '',
    ...(loggedInCustomer?.sharedPoolPriorities || []).map((p) => p.customerId),
  ];
  const priorityByCustomer = new Map<string, number>(
    customers.map((customerId, index) => [customerId, index + 1]),
  );

  let organisation = Organisations.findOne({
    'departments.id': job.department.id,
  });
  let dbDepartment = organisation?.departments.find(
    (dep) => dep.id === job.department.id,
  );
  if (!dbDepartment) {
    throw new Error('Department not found');
  }
  const users = queryPossibleUsersForJob({
    date,
    job,
    organisation,
    department: dbDepartment,
    shifts,
    loggedInCustomer,
    sharedPoolCustomers,
    isOwnJob,
    undecided,
    ignoreUserPreferenceDepartmentBlacklist,
    jobInPast,
    search,
    transferToMe,
  });

  const filteredUsers = filterPossibleUsersForJob({
    users,
    date,
    shifts,
    poolerMap,
    userCustomerId: user.customerId,
  });

  return filteredUsers
    .map(
      (poolerUser): JobMatchPoolerUser =>
        getJobMatchPooler({
          poolerUser,
          job,
          date,
          jobShifts: shifts,
          department,
          loggedInCustomer,
          poolerMap,
          i18n,
        }),
    )
    .filter((user) => !user.isSharedPooler || user.restStatus?.hasRested)
    .sort(
      (a, b) =>
        (priorityByCustomer.get(a.sourceCustomer?.id || '') || 99) -
        (priorityByCustomer.get(b.sourceCustomer?.id || '') || 99),
    );
}
